import React, { useContext, useMemo, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useDebounce, useFeatureFlags } from '@node-space/hooks'
import { Box, Button, EmptyState, Icon, Input, RowsSkeletonLoader } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useContactsInfiniteQuery } from 'hooks/queries/useContactsInfiniteQuery'
import {
  contactActionAtom,
  contactInstrumentActionAtom,
  contactInstrumentAtom,
  editingContactInstrumentAtom,
  massPayoutContactActionAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { ContactsSheets } from 'pages/Contacts/components/ContactsSheets'
import { stripSpecialCharacters } from 'pages/Contacts/utils/utils'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ContactActionIds } from 'types/atoms/atom.contacts'
import { ContactEntityType } from 'types/contacts'
import { getBeneficiaryToContactEntityMapping } from 'utils/beneficiaries'
import { ContactsSearchList } from './ContactsSearchList'

interface ContactsSearchProps {
  showAddContact?: boolean
}

export const ContactsSearch = ({ showAddContact = true }: ContactsSearchProps) => {
  const { t } = useTranslation()
  const { enableContactBankAccounts } = useFeatureFlags()
  const [walletAction] = useContext(WalletActionContext)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchTerm = useDebounce(searchQuery, SEARCH_INPUT_DEBOUNCE_TIME)
  const [hasLoadedMore, setHasLoadedMore] = useState(false)
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)
  const [contactAction, setContactAction] = useAtom(contactActionAtom)
  const massPayoutContactAction = useAtomValue(massPayoutContactActionAtom)
  const contactInstrumentAction = useAtomValue(contactInstrumentActionAtom)
  const [contactIds, setContactIds] = useState<string[]>(walletAction?.meta?.contactFilter)
  const setContactInstrument = useSetAtom(contactInstrumentAtom)
  const setIsEditingContactInstrument = useSetAtom(editingContactInstrumentAtom)

  const entityTypeFilter: ContactEntityType = useMemo(() => {
    if (!contactAction?.meta?.contactBeneficiary) return

    return getBeneficiaryToContactEntityMapping(contactAction?.meta?.contactBeneficiary)
  }, [contactAction?.meta?.contactBeneficiary])

  const {
    data: contacts,
    fetchNextPage,
    hasNextPage,
    isError,
    isPending,
    isFetchingNextPage,
  } = useContactsInfiniteQuery({
    searchTerm: debouncedSearchTerm,
    contactIds: contactIds,
    type: entityTypeFilter,
  })

  const hasNoResults = debouncedSearchTerm && !contacts?.length
  const showEmpty = !isPending && !isError && !contacts?.length && !debouncedSearchTerm
  const showError = isError && !isPending
  const showNoResults = hasNoResults && !isPending
  const showSearch = !!contacts?.length || hasNoResults
  const handleAddContact = () => {
    if (walletAction?.meta?.contactInstrument) {
      setContactInstrument(walletAction?.meta?.contactInstrument)
      setIsEditingContactInstrument(false)
    }
    setContactAction({
      ...contactAction,
      actionId: ContactActionIds.CONTACT,
      header: t(enableContactBankAccounts ? 'contacts.addContact' : 'contacts.addCryptoContact'),
      onClose: () => {
        setIsContactSheetOpen(false)
        contactAction?.onClose?.()
      },
      meta: {
        contactFilter: walletAction?.meta?.contactFilter,
        enableCreateInstrument: walletAction?.meta?.enableCreateInstrument,
        handleContactInstrumentUpdate: walletAction?.meta?.handleContactInstrumentUpdate,
        contactBeneficiary: contactAction?.meta?.contactBeneficiary,
      },
    })
    setIsContactSheetOpen(true)
  }

  return (
    <>
      <Box paddingT={24} className="max-h-[75vh]">
        {/* Show error state */}
        {showError && (
          <EmptyState
            testId="error-state"
            LottiePlayer={<Lottie src="connection-error" />}
            title={t('defaultError')}
            description={
              <>
                {`${t('requestErrors.comeBackLater')} `}{' '}
                <Button variant="ghost" noPadding onClick={() => window.location.reload()}>
                  {t('requestErrors.refreshPage')}
                </Button>
              </>
            }
          />
        )}
        {/* Show content state */}
        {!isError && (
          <>
            {showSearch && (
              <Box paddingB={16} width="full" justify="between" flex gap={12}>
                <Box background="standard" width={'full'}>
                  <Input
                    placeholderText={t('contacts.searchPlaceholder')}
                    onChange={event => setSearchQuery(stripSpecialCharacters(event.target.value))}
                    searchable={true}
                    testId="contact-search-field"
                  />
                </Box>
                {showAddContact && (
                  <Button testId="add-contact" variant="secondary" onClick={handleAddContact}>
                    {t('payments.addContact')}
                  </Button>
                )}
              </Box>
            )}

            {isPending && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

            {showNoResults && (
              <NoSearchResults
                clearSearchCriteria={() => {
                  setSearchQuery('')
                }}
              />
            )}

            {!!contacts?.length && (
              <Box className="w-full">
                <ContactsSearchList
                  handleContactSelect={
                    walletAction?.meta?.handleContactSelect ??
                    contactAction?.meta?.handleContactSelect ??
                    contactInstrumentAction?.meta?.handleContactSelect ??
                    massPayoutContactAction?.meta?.handleContactSelect
                  }
                  isFetchingNextPage={isFetchingNextPage}
                  contacts={contacts}
                  hasLoadedMore={hasLoadedMore}
                  fetchNextPage={fetchNextPage}
                  setHasLoadedMore={setHasLoadedMore}
                  hasNextPage={hasNextPage}
                  contactFilter={contactIds}
                  setContactIds={setContactIds}
                />
              </Box>
            )}
          </>
        )}
        {/* Show empty state */}
        {showEmpty && (
          <EmptyState
            testId="empty-state"
            LottiePlayer={<Lottie src="no-data" />}
            title={t(
              enableContactBankAccounts
                ? 'contacts.noContactHeadlineV2'
                : 'contacts.noContactHeadline'
            )}
            description={t(
              enableContactBankAccounts
                ? 'contacts.addContactDescriptionV2'
                : 'contacts.addContactDescription'
            )}
            PrimaryButton={
              <Button
                testId="add-contact"
                iconElement={<Icon name="Plus" />}
                onClick={handleAddContact}
              >
                {t('contacts.addContact')}
              </Button>
            }
          />
        )}
      </Box>
      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet />
    </>
  )
}
